import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../institutionAdmin.endpoints";
import type {
  AdmissionInquiry,
  CreateInquiryPayload,
  ConvertInquiryPayload,
  DirectAdmissionPayload,
  TransferAdmissionPayload,
  ReadmissionPayload,
  AdmissionResult,
  AdmissionFeeSummary,
  InquiryStatus,
  InquiryFormField,
  InquiryChannel,
} from "../types";

const ADMISSIONS = INSTITUTION_ADMIN_ENDPOINTS.ADMISSIONS;

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export async function fetchInquiries(
  institutionId: number,
  params?: { status?: InquiryStatus; channel?: InquiryChannel; page?: number; limit?: number }
): Promise<{ inquiries: AdmissionInquiry[]; pagination: Pagination }> {
  const { data } = await privateClient.get(ADMISSIONS.INQUIRIES.LIST(institutionId), { params });
  return data;
}

export async function fetchInquiryForm(institutionId: number): Promise<InquiryFormField[]> {
  const { data } = await privateClient.get(`${ADMISSIONS.INQUIRIES.LIST(institutionId).replace(/\/inquiries$/, "")}/inquiry-form`);
  return data.fields;
}

export async function createInquiry(institutionId: number, payload: CreateInquiryPayload): Promise<AdmissionInquiry> {
  const { data } = await privateClient.post(ADMISSIONS.INQUIRIES.CREATE(institutionId), payload);
  return data.inquiry;
}

export async function updateInquiryStatus(
  institutionId: number,
  inquiryId: number,
  status: InquiryStatus
): Promise<AdmissionInquiry> {
  const { data } = await privateClient.patch(ADMISSIONS.INQUIRIES.STATUS(institutionId, inquiryId), { status });
  return data.inquiry;
}

export async function convertInquiry(
  institutionId: number,
  inquiryId: number,
  payload: ConvertInquiryPayload
): Promise<AdmissionResult> {
  const { data } = await privateClient.post(ADMISSIONS.INQUIRIES.CONVERT(institutionId, inquiryId), payload);
  return data;
}

export async function admitDirect(institutionId: number, payload: DirectAdmissionPayload): Promise<AdmissionResult> {
  const { data } = await privateClient.post(ADMISSIONS.DIRECT(institutionId), payload);
  return data;
}

export async function admitTransfer(institutionId: number, payload: TransferAdmissionPayload): Promise<AdmissionResult> {
  const { data } = await privateClient.post(ADMISSIONS.TRANSFER(institutionId), payload);
  return data;
}

export async function readmitStudent(
  institutionId: number,
  studentId: number,
  payload: ReadmissionPayload
): Promise<AdmissionResult> {
  const { data } = await privateClient.post(ADMISSIONS.READMIT(institutionId, studentId), payload);
  return data;
}

export async function fetchRegistrationPreview(institutionId: number): Promise<string> {
  const { data } = await privateClient.get(ADMISSIONS.REGISTRATION_PREVIEW(institutionId));
  return data.registrationNumber;
}

export interface LoginAvailability {
  domain: string | null;
  email?: string | null;
  available?: boolean;
  reason?: "TAKEN" | "INVALID" | null;
}

/** The school's login domain, and (with a username) the email it makes and whether it is free. */
export async function fetchLoginAvailability(institutionId: number, username?: string, studentId?: number): Promise<LoginAvailability> {
  try {
    const { data } = await privateClient.get(ADMISSIONS.LOGIN_AVAILABILITY(institutionId), { params: { username: username || undefined, studentId } });
    return data;
  } catch {
    return { domain: null };
  }
}

export async function fetchAdmissionFee(institutionId: number, studentId: number): Promise<AdmissionFeeSummary> {
  const { data } = await privateClient.get(ADMISSIONS.ADMISSION_FEE(institutionId, studentId));
  return data.admissionFee;
}

export async function releaseCredentials(
  institutionId: number,
  studentId: number,
   overrideReason?: string,
  username?: string
): Promise<{ credential: { email: string; tempPassword: string }; admissionFeeStatus: "PAID" | "OVERRIDDEN" }> {
  const { data } = await privateClient.post(ADMISSIONS.RELEASE_CREDENTIALS(institutionId, studentId), { overrideReason, username });
  return data;
}
